import * as Y from "yjs";
import * as awarenessProtocol from "y-protocols/awareness";
import type { TacticalFeature } from "@/services/tactical.service";
import { base64ToUint8, colorFromUserId, uint8ToBase64 } from "./yjsCodec";
import type { RemoteCursor, TacticalAwarenessRoom } from "./tacticalAwareness";

export type CollabDraft = {
  clientId: number;
  userId: string;
  label: string;
  color: string;
  kind: "line" | "polygon";
  coordinates: [number, number][];
};

export type TacticalCollabRoom = TacticalAwarenessRoom & {
  seedFeatures: (features: TacticalFeature[]) => void;
  upsertFeature: (feature: TacticalFeature) => void;
  removeFeature: (featureId: number) => void;
  setLocalDraft: (
    draft: { kind: "line" | "polygon"; coordinates: [number, number][] } | null,
  ) => void;
  getRemoteDrafts: () => CollabDraft[];
  applyRemoteDocUpdate: (updateB64: string) => void;
  listFeaturesFromDoc: () => TacticalFeature[];
};

/**
 * Y.Doc комнаты доски: Awareness (курсоры) + Map features + Map drafts.
 * PostGIS/REST остаётся SoT; Doc — realtime mirror.
 */
export function createTacticalCollabRoom(opts: {
  userId: string;
  label: string;
  onLocalAwarenessEncoded: (updateB64: string) => void;
  onLocalDocEncoded: (updateB64: string) => void;
  onFeaturesMirrorChanged?: (features: TacticalFeature[]) => void;
  onDraftsChanged?: () => void;
}): TacticalCollabRoom {
  const doc = new Y.Doc();
  const awareness = new awarenessProtocol.Awareness(doc);
  const featuresMap = doc.getMap<string>("features");
  const draftsMap = doc.getMap<string>("drafts");
  const color = colorFromUserId(opts.userId);
  const label = opts.label || opts.userId.slice(0, 8);

  awareness.setLocalStateField("user", {
    id: opts.userId,
    label,
    color,
  });

  const onAwarenessUpdate = (
    { added, updated, removed }: { added: number[]; updated: number[]; removed: number[] },
    origin: unknown,
  ) => {
    if (origin === "remote") return;
    const changed = added.concat(updated, removed);
    if (changed.length === 0) return;
    const encoded = awarenessProtocol.encodeAwarenessUpdate(awareness, changed);
    opts.onLocalAwarenessEncoded(uint8ToBase64(encoded));
  };
  awareness.on("update", onAwarenessUpdate);

  const onDocUpdate = (update: Uint8Array, origin: unknown) => {
    if (origin === "remote" || origin === "silent") return;
    opts.onLocalDocEncoded(uint8ToBase64(update));
  };
  doc.on("update", onDocUpdate);

  const parseFeature = (raw: string): TacticalFeature | null => {
    try {
      return JSON.parse(raw) as TacticalFeature;
    } catch {
      return null;
    }
  };

  const listFeaturesFromDoc = (): TacticalFeature[] => {
    const out: TacticalFeature[] = [];
    featuresMap.forEach((raw) => {
      const f = parseFeature(raw);
      if (f) out.push(f);
    });
    return out;
  };

  const emitFeatures = () => {
    opts.onFeaturesMirrorChanged?.(listFeaturesFromDoc());
  };

  featuresMap.observe((_ev, tr) => {
    // silent seed и local upsert не перезаписывают React Query (SoT — REST/кэш).
    if (tr.origin === "remote") emitFeatures();
  });
  draftsMap.observe((_ev, tr) => {
    if (tr.origin === "silent") return;
    opts.onDraftsChanged?.();
  });

  let lastSentAt = 0;
  const setLocalCursor = (lng: number, lat: number) => {
    const now = Date.now();
    if (now - lastSentAt < 80) return;
    lastSentAt = now;
    awareness.setLocalStateField("cursor", { lng, lat });
  };

  const applyRemoteAwareness = (updateB64: string) => {
    try {
      const bytes = base64ToUint8(updateB64);
      awarenessProtocol.applyAwarenessUpdate(awareness, bytes, "remote");
    } catch {
      /* ignore */
    }
  };

  const applyRemoteDocUpdate = (updateB64: string) => {
    try {
      const bytes = base64ToUint8(updateB64);
      Y.applyUpdate(doc, bytes, "remote");
    } catch {
      /* ignore */
    }
  };

  const getRemoteCursors = (): RemoteCursor[] => {
    const localId = awareness.clientID;
    const out: RemoteCursor[] = [];
    awareness.getStates().forEach((state, clientId) => {
      if (clientId === localId) return;
      const user = state.user as { id?: string; label?: string; color?: string } | undefined;
      const cursor = state.cursor as { lng?: number; lat?: number } | undefined;
      if (
        !user?.id ||
        typeof cursor?.lng !== "number" ||
        typeof cursor?.lat !== "number" ||
        !Number.isFinite(cursor.lng) ||
        !Number.isFinite(cursor.lat)
      ) {
        return;
      }
      out.push({
        clientId,
        userId: user.id,
        label: user.label || user.id.slice(0, 8),
        color: user.color || colorFromUserId(user.id),
        lng: cursor.lng,
        lat: cursor.lat,
      });
    });
    return out;
  };

  const seedFeatures = (features: TacticalFeature[]) => {
    doc.transact(() => {
      const seen = new Set<string>();
      for (const f of features) {
        const id = f.properties?.id;
        if (id == null) continue;
        const key = String(id);
        seen.add(key);
        featuresMap.set(key, JSON.stringify(f));
      }
      const toDelete: string[] = [];
      featuresMap.forEach((_, key) => {
        if (!seen.has(key)) toDelete.push(key);
      });
      for (const key of toDelete) featuresMap.delete(key);
    }, "silent");
  };

  const upsertFeature = (feature: TacticalFeature) => {
    const id = feature.properties?.id;
    if (id == null) return;
    doc.transact(() => {
      featuresMap.set(String(id), JSON.stringify(feature));
    }, "local");
  };

  const removeFeature = (featureId: number) => {
    doc.transact(() => {
      featuresMap.delete(String(featureId));
    }, "local");
  };

  const setLocalDraft = (
    draft: { kind: "line" | "polygon"; coordinates: [number, number][] } | null,
  ) => {
    const key = String(awareness.clientID);
    doc.transact(() => {
      if (!draft || draft.coordinates.length === 0) {
        draftsMap.delete(key);
        return;
      }
      draftsMap.set(
        key,
        JSON.stringify({
          userId: opts.userId,
          label,
          color,
          kind: draft.kind,
          coordinates: draft.coordinates,
        }),
      );
    }, "local");
  };

  const getRemoteDrafts = (): CollabDraft[] => {
    const localId = awareness.clientID;
    const out: CollabDraft[] = [];
    draftsMap.forEach((raw, key) => {
      const clientId = Number(key);
      if (clientId === localId) return;
      try {
        const parsed = JSON.parse(raw) as {
          userId?: string;
          label?: string;
          color?: string;
          kind?: "line" | "polygon";
          coordinates?: [number, number][];
        };
        if (
          !parsed.userId ||
          (parsed.kind !== "line" && parsed.kind !== "polygon") ||
          !Array.isArray(parsed.coordinates) ||
          parsed.coordinates.length < 1
        ) {
          return;
        }
        out.push({
          clientId: Number.isFinite(clientId) ? clientId : 0,
          userId: parsed.userId,
          label: parsed.label || parsed.userId.slice(0, 8),
          color: parsed.color || colorFromUserId(parsed.userId),
          kind: parsed.kind,
          coordinates: parsed.coordinates,
        });
      } catch {
        /* ignore */
      }
    });
    return out;
  };

  const destroy = () => {
    awareness.off("update", onAwarenessUpdate);
    doc.off("update", onDocUpdate);
    try {
      draftsMap.delete(String(awareness.clientID));
    } catch {
      /* ignore */
    }
    awarenessProtocol.removeAwarenessStates(awareness, [awareness.clientID], "local");
    awareness.destroy();
    doc.destroy();
  };

  return {
    doc,
    awareness,
    applyRemoteAwareness,
    setLocalCursor,
    getRemoteCursors,
    destroy,
    seedFeatures,
    upsertFeature,
    removeFeature,
    setLocalDraft,
    getRemoteDrafts,
    applyRemoteDocUpdate,
    listFeaturesFromDoc,
  };
}
